"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ComboBoxMenu = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const classnames_1 = __importDefault(require("classnames"));
const React = __importStar(require("react"));
const index_js_1 = require("../Menu/index.js");
const index_js_2 = require("../Surface/index.js");
const index_js_3 = require("../utils/index.js");
const helpers_js_1 = require("./helpers.js");
const isOverflowOverlaySupported = () => { var _a, _b, _c; return (_c = (_b = (_a = (0, index_js_3.getWindow)()) === null || _a === void 0 ? void 0 : _a.CSS) === null || _b === void 0 ? void 0 : _b.supports) === null || _c === void 0 ? void 0 : _c.call(_b, 'overflow: overlay'); };
const VirtualizedComboBoxMenu = React.forwardRef(({ children, className, style, ...rest }, forwardedRef) => {
    const { minWidth, id, filteredOptions, getMenuItem, focusedIndex } = (0, index_js_3.useSafeContext)(helpers_js_1.ComboBoxStateContext);
    const { menuRef } = (0, index_js_3.useSafeContext)(helpers_js_1.ComboBoxRefsContext);
    const virtualItemRenderer = React.useCallback((index) => filteredOptions.length > 0
        ? getMenuItem(filteredOptions[index], index)
        : children, // Here is expected empty state content
    [filteredOptions, getMenuItem, children]);
    const focusedVisibleIndex = React.useMemo(() => {
        var _a, _b;
        const currentElement = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(`[data-iui-index="${focusedIndex}"]`);
        if (!currentElement) {
            return focusedIndex;
        }
        return Number((_b = currentElement.getAttribute('data-iui-filtered-index')) !== null && _b !== void 0 ? _b : focusedIndex);
    }, [focusedIndex, menuRef]);
    const { outerProps, innerProps, visibleChildren } = (0, index_js_3.useVirtualization)({
        // 'Fool' VirtualScroll by passing length 1
        // whenever there is no elements, to show empty state message
        itemsLength: filteredOptions.length || 1,
        itemRenderer: virtualItemRenderer,
        scrollToIndex: focusedVisibleIndex,
    });
    const surfaceStyles = {
        minWidth,
        // set as constant because we don't want it shifting when items are unmounted
        maxWidth: minWidth,
        // max-height must be on the outermost element for virtual scroll
        maxHeight: 'calc((var(--iui-component-height) - 1px) * 8.5)',
        overflowY: isOverflowOverlaySupported() ? 'overlay' : 'auto',
        ...style,
    };
    return (React.createElement(index_js_2.Surface, { style: surfaceStyles, ...rest },
        React.createElement("div", { ...outerProps },
            React.createElement(index_js_1.Menu, { id: `${id}-list`, setFocus: false, role: 'listbox', ref: (0, index_js_3.mergeRefs)(menuRef, innerProps.ref, forwardedRef), className: className, style: innerProps.style }, visibleChildren))));
});
exports.ComboBoxMenu = React.forwardRef((props, forwardedRef) => {
    const { className, style, ...rest } = props;
    const { minWidth, id, enableVirtualization } = (0, index_js_3.useSafeContext)(helpers_js_1.ComboBoxStateContext);
    const { menuRef } = (0, index_js_3.useSafeContext)(helpers_js_1.ComboBoxRefsContext);
    const refs = (0, index_js_3.useMergedRefs)(menuRef, forwardedRef);
    const styles = React.useMemo(() => ({
        minWidth,
        maxWidth: `min(${minWidth * 2}px, 90vw)`,
    }), [minWidth]);
    return (React.createElement(React.Fragment, null, !enableVirtualization ? (React.createElement(index_js_1.Menu, { id: `${id}-list`, style: { ...styles, ...style }, setFocus: false, role: 'listbox', ref: refs, className: (0, classnames_1.default)('iui-scroll', className), ...rest })) : (React.createElement(VirtualizedComboBoxMenu, { ref: forwardedRef, ...props }))));
});
exports.ComboBoxMenu.displayName = 'ComboBoxMenu';
